/**
 * Header Hooks - Extended with siteId lookup
 *
 * @override
 * - Re-exports all base header hooks from @amblique/contentful
 * - Adds useHeaderBySiteId hook for fetching header by site identifier
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseHeaderBySiteId,
  getHeaderBySiteId,
  getFullHeaderBySiteId,
} from '@amblique/king-living/app/contentful/queries/header.queries';

// Re-export all base hooks
export * from '@amblique/contentful/app/contentful/hooks/use-header';

/**
 * Hook for fetching a Header by its siteId field
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz')
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Object} [options.variables={}] - Additional GraphQL query variables
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.Header|null, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.Header|null, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: header, isLoading } = useHeaderBySiteId('kl-au');
 *
 * @example
 * // With variant selection
 * const { data } = useHeaderBySiteId('kl-nz', {
 *   variant: 'full',  // Get complete nested data
 *   variables: { preview: true }
 * });
 *
 * @example
 * // With all options
 * const { data } = useHeaderBySiteId('kl-au', {
 *   variant: 'base',  // Fast, scalars only
 *   variables: { preview: true },
 *   queryOptions: { staleTime: 0 },
 *   requestOptions: { cache: 'no-store' }
 * });
 */
export function useHeaderBySiteId(siteId, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseHeaderBySiteId :
    variant === 'full' ?
      getFullHeaderBySiteId :
      getHeaderBySiteId;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'header',
      'siteId',
      siteId,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          siteId,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.headerCollection?.items?.[0] || null;
    },

    enabled: !!contentfulService && !!siteId,

    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}
